import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import type {
  IWorkJourneyManagementCalendarCell,
  IWorkJourneyManagementDayReportJourneyItem,
  IWorkJourneyManagementDayReportSummary,
  IWorkJourneyManagementDayReportUserItem,
} from '@src/models/interfaces/workJourneyManagement.interface';
import WorkJourneyManagementService from '@src/services/WorkJourneyManagementService';

import {
  WORK_JOURNEY_DAY_REPORT_PAGE_SIZE,
  WORK_JOURNEY_MANAGEMENT_JOURNEY_FILTER_ALL,
} from '../../../constants';
import { useWorkJourneyManagementContext } from '../../../context/WorkJourneyManagementContext';

const EMPTY_SUMMARY: IWorkJourneyManagementDayReportSummary = {
  present: 0,
  justifiedAbsence: 0,
  unjustifiedAbsence: 0,
  journeys: 0,
  people: 0,
};

export type DayReportJourneyDetailState = {
  loading: boolean;
  error: string | null;
  items: IWorkJourneyManagementDayReportUserItem[];
};

export type UseWorkJourneyCalendarDayReportDrawerResult = {
  open: boolean;
  date: string | null;
  journeys: IWorkJourneyManagementDayReportJourneyItem[];
  summary: IWorkJourneyManagementDayReportSummary;
  totalJourneys: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  expandedJourneyId: string | null;
  detailByJourneyId: Record<string, DayReportJourneyDetailState>;
  openForCell: (cell: IWorkJourneyManagementCalendarCell) => void;
  close: () => void;
  setPage: (page: number) => void;
  toggleJourneyExpand: (workJourneyConfigId: string) => void;
};

/**
 * Past-day "View report" drawer: loads paginated journeys + totals, then
 * lazy-loads users per journey via `listDayReportDetail` on expand.
 */
export const useWorkJourneyCalendarDayReportDrawer =
  (): UseWorkJourneyCalendarDayReportDrawerResult => {
    const intl = useIntl();
    const { orgId, journeyFilter, calendarUserId } = useWorkJourneyManagementContext();
    const [date, setDate] = useState<string | null>(null);
    const [journeys, setJourneys] = useState<IWorkJourneyManagementDayReportJourneyItem[]>([]);
    const [summary, setSummary] = useState<IWorkJourneyManagementDayReportSummary>(EMPTY_SUMMARY);
    const [totalJourneys, setTotalJourneys] = useState(0);
    const [page, setPageState] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedJourneyId, setExpandedJourneyId] = useState<string | null>(null);
    const [detailByJourneyId, setDetailByJourneyId] = useState<
      Record<string, DayReportJourneyDetailState>
    >({});
    const listRequestIdRef = useRef(0);
    const detailRequestIdsRef = useRef<Record<string, number>>({});
    const listFiltersRef = useRef({ journeyFilter, calendarUserId });
    const serviceRef = useRef(new WorkJourneyManagementService());
    const pageSize = WORK_JOURNEY_DAY_REPORT_PAGE_SIZE;

    const close = useCallback(() => {
      listRequestIdRef.current += 1;
      detailRequestIdsRef.current = {};
      setDate(null);
      setJourneys([]);
      setSummary(EMPTY_SUMMARY);
      setTotalJourneys(0);
      setPageState(1);
      setError(null);
      setLoading(false);
      setExpandedJourneyId(null);
      setDetailByJourneyId({});
    }, []);

    const openForCell = useCallback((cell: IWorkJourneyManagementCalendarCell) => {
      if (!cell?.date || cell.footerAction !== 'report') return;
      setPageState(1);
      setExpandedJourneyId(null);
      setDetailByJourneyId({});
      detailRequestIdsRef.current = {};
      setDate(cell.date);
    }, []);

    const setPage = useCallback((nextPage: number) => {
      setExpandedJourneyId(null);
      setPageState(Math.max(1, nextPage));
    }, []);

    const loadJourneyDetail = useCallback(
      async (workJourneyConfigId: string, reportDate: string) => {
        if (!orgId) return;

        const requestId = (detailRequestIdsRef.current[workJourneyConfigId] ?? 0) + 1;
        detailRequestIdsRef.current[workJourneyConfigId] = requestId;
        setDetailByJourneyId(previous => ({
          ...previous,
          [workJourneyConfigId]: {
            loading: true,
            error: null,
            items: previous[workJourneyConfigId]?.items ?? [],
          },
        }));

        try {
          const response = await serviceRef.current.listDayReportDetail({
            organizationId: orgId,
            date: reportDate,
            workJourneyConfigId,
            ...(calendarUserId ? { userId: calendarUserId } : {}),
          });
          if (requestId !== detailRequestIdsRef.current[workJourneyConfigId]) return;
          setDetailByJourneyId(previous => ({
            ...previous,
            [workJourneyConfigId]: {
              loading: false,
              error: null,
              items: response.items,
            },
          }));
        } catch {
          if (requestId !== detailRequestIdsRef.current[workJourneyConfigId]) return;
          setDetailByJourneyId(previous => ({
            ...previous,
            [workJourneyConfigId]: {
              loading: false,
              error: intl.formatMessage({ id: 'general.save.error.message' }),
              items: [],
            },
          }));
        }
      },
      [calendarUserId, intl, orgId],
    );

    const toggleJourneyExpand = useCallback(
      (workJourneyConfigId: string) => {
        if (!date) return;

        if (expandedJourneyId === workJourneyConfigId) {
          setExpandedJourneyId(null);
          return;
        }

        setExpandedJourneyId(workJourneyConfigId);
        const cached = detailByJourneyId[workJourneyConfigId];
        // Skip only a completed success cache; refetch when loading/error/missing.
        if (!cached || cached.loading || cached.error) {
          void loadJourneyDetail(workJourneyConfigId, date);
        }
      },
      [date, detailByJourneyId, expandedJourneyId, loadJourneyDetail],
    );

    useEffect(() => {
      if (!date || !orgId) {
        return undefined;
      }

      const filtersChanged =
        listFiltersRef.current.journeyFilter !== journeyFilter ||
        listFiltersRef.current.calendarUserId !== calendarUserId;
      listFiltersRef.current = { journeyFilter, calendarUserId };

      if (filtersChanged) {
        setExpandedJourneyId(null);
        setDetailByJourneyId({});
        detailRequestIdsRef.current = {};
        if (page !== 1) {
          setPageState(1);
          return undefined;
        }
      }

      const requestId = ++listRequestIdRef.current;
      setLoading(true);
      setError(null);

      const load = async () => {
        try {
          const response = await serviceRef.current.listDayReport({
            organizationId: orgId,
            date,
            workJourneyConfigId:
              journeyFilter === WORK_JOURNEY_MANAGEMENT_JOURNEY_FILTER_ALL
                ? undefined
                : journeyFilter,
            ...(calendarUserId ? { userId: calendarUserId } : {}),
            from: (page - 1) * pageSize,
            pageSize,
          });
          if (requestId !== listRequestIdRef.current) return;
          setJourneys(response.items);
          setSummary(response.summary ?? EMPTY_SUMMARY);
          setTotalJourneys(response.total);
        } catch {
          if (requestId !== listRequestIdRef.current) return;
          setJourneys([]);
          setSummary(EMPTY_SUMMARY);
          setTotalJourneys(0);
          setError(intl.formatMessage({ id: 'general.save.error.message' }));
        } finally {
          if (requestId === listRequestIdRef.current) {
            setLoading(false);
          }
        }
      };

      void load();
      return undefined;
    }, [date, orgId, journeyFilter, calendarUserId, intl, page, pageSize]);

    return {
      open: Boolean(date),
      date,
      journeys,
      summary,
      totalJourneys,
      page,
      pageSize,
      loading,
      error,
      expandedJourneyId,
      detailByJourneyId,
      openForCell,
      close,
      setPage,
      toggleJourneyExpand,
    };
  };
